import {useSocket} from "@/utils/websocket";
import {showNotify, showWhatsAppNotify} from "@/utils/notifies";
import router from "@/router";
import {truncateByBytes} from "@/utils/notifies/tool";
import {Storage} from "@/utils/storage";

let messageQueue = []; // 用于存储消息的队列
let messageQueueTimer = null;
let activeSocket = null;
let activeSocketUrl = '';
let activeSocketHandlers = null;
let activeStore = null;

const EMAIL_UNREAD_DOT_STORAGE_KEY = 'email_unread_dot'
const EMAIL_WS_UNREAD_COUNT_STORAGE_KEY = 'email_ws_unread_count'

const WA_JID_MAP_STORAGE_KEY = 'wa_jid_map'

function normalizeJid(val) {
    const s = String(val || '').trim()
    return s
}

function mapLidToPnCached(jid) {
    const s = normalizeJid(jid)
    if (!s || !s.endsWith('@lid')) return ''
    try {
        const cached = Storage.get(WA_JID_MAP_STORAGE_KEY)
        const pn = cached?.lidToPn?.[s]
        return typeof pn === 'string' ? pn : ''
    } catch (e) {
        return ''
    }
}

function mapJidByCache(jid) {
    const s = normalizeJid(jid)
    if (!s) return ''
    if (s.endsWith('@lid')) {
        const pn = mapLidToPnCached(s)
        return pn || s
    }
    return s
}

function getSocketUrl(userId) {
    const baseApi = String(process.env.VUE_APP_BASE_API || '');
    return `${baseApi.replace("https://", "wss://").replace("http://", "ws://")}/v3/websocket/${userId}`;
}

function disposeMessageQueueTimer() {
    if (messageQueueTimer) {
        clearInterval(messageQueueTimer);
        messageQueueTimer = null;
    }
}

function processMessageQueue() {
    if (!activeStore || !messageQueue.length) {
        return;
    }

    window.setTimeout(() => {
        if (!activeStore || !messageQueue.length) {
            return;
        }

        console.log('show', messageQueue);
        activeStore.commit("message/setEmailData", messageQueue);

        if (messageQueue[0]) {
            const delta = Number(messageQueue.length || 0) || 0

            try {
                localStorage.setItem(EMAIL_UNREAD_DOT_STORAGE_KEY, '1')
            } catch (e) {
            }

            try {
                const raw = localStorage.getItem(EMAIL_WS_UNREAD_COUNT_STORAGE_KEY)
                const prev = Number(raw || 0)
                const next = (Number.isFinite(prev) ? prev : 0) + delta
                localStorage.setItem(EMAIL_WS_UNREAD_COUNT_STORAGE_KEY, String(next))
                window.dispatchEvent(new CustomEvent('email-ws-unread-count', {detail: {count: next, delta}}));
            } catch (e) {
            }

            try {
                window.dispatchEvent(new CustomEvent('email-unread-dot', {detail: {hasUnread: true}}));
            } catch (e) {
            }

            messageQueue[0].msgTxt = truncateByBytes(messageQueue[0]?.msgTxt, 60);
            showNotify(messageQueue.length, messageQueue[0], () => {
                router.push('/email/inbox');
            });
            messageQueue = [];
        }
    });
}

function ensureMessageQueueTimer(store) {
    activeStore = store || activeStore;
    if (!messageQueueTimer) {
        messageQueueTimer = setInterval(processMessageQueue, 500);
    }
}

function unbindActiveSocket() {
    if (!activeSocket || !activeSocketHandlers) {
        return;
    }

    activeSocket.off('open', activeSocketHandlers.open);
    activeSocket.off('close', activeSocketHandlers.close);
    activeSocket.off('error', activeSocketHandlers.error);
    activeSocket.off('message', activeSocketHandlers.message);
    activeSocketHandlers = null;
}

function bindActiveSocket(socket, userId, store) {
    if (!socket || !store) {
        return;
    }

    if (activeSocketHandlers?.socket === socket && activeSocketHandlers?.store === store) {
        return;
    }

    unbindActiveSocket();

    const openHandler = () => {
        console.log('[WA-WS] connected', {userId: String(userId || '')});
    };

    const closeHandler = () => {
        console.log('[WA-WS] disconnected', {userId: String(userId || '')});
    };

    const errorHandler = (err) => {
        console.error('[WA-WS] error', err);
    };

    const messageHandler = (data) => {
        if (data === "ping") {
            return;
        }

        let targetObjData = null;
        try {
            targetObjData = JSON.parse(data);
        } catch (e) {
            console.error('[WA-WS] invalid message (not JSON)', data);
            return;
        }

        const msgType = String(
            (targetObjData && targetObjData.messageType != null)
                ? targetObjData.messageType
                : (targetObjData && targetObjData.msgType != null)
                    ? targetObjData.msgType
                    : (targetObjData && targetObjData.type != null)
                        ? targetObjData.type
                        : ''
        ).trim().toLowerCase()

        const msgEvent = String(
            (targetObjData && targetObjData.event != null)
                ? targetObjData.event
                : (targetObjData && targetObjData.action != null)
                    ? targetObjData.action
                    : ''
        ).trim().toLowerCase()

        const looksLikeWaMessage = (() => {
            const msgType = String(targetObjData?.messageType || targetObjData?.msgType || targetObjData?.type || '')
            const event = String(targetObjData?.event || targetObjData?.action || '')

            const lowerMsgType = msgType.toLowerCase()
            const lowerEvent = event.toLowerCase()
            const isWa = lowerMsgType === 'whatsapp' || lowerMsgType === 'whatsapp'.replace('app', 'app') || lowerEvent.includes('wa')
            if (isWa) return true

            // Fallback: backend may omit messageType/event but still send WA payload fields
            const hasWaIds = Boolean(targetObjData?.chatId || targetObjData?.from || targetObjData?.to)
            const hasMessageId = Boolean(targetObjData?.messageId)
            const hasText = typeof targetObjData?.message === 'string'
                ? targetObjData.message.trim().length > 0
                : typeof targetObjData?.content === 'string'
                    ? targetObjData.content.trim().length > 0
                    : typeof targetObjData?.text === 'string'
                        ? targetObjData.text.trim().length > 0
                        : false
            const hasAttachment = Boolean(
                (Array.isArray(targetObjData?.files) && targetObjData.files.length) ||
                (Array.isArray(targetObjData?.attachments) && targetObjData.attachments.length) ||
                (Array.isArray(targetObjData?.media) && targetObjData.media.length) ||
                targetObjData?.file ||
                targetObjData?.attachment
            )
            return hasWaIds && hasMessageId && (hasText || hasAttachment)
        })()

        if (looksLikeWaMessage) {
            const rawDerivedChatId = String(
                targetObjData.chatId ||
                targetObjData.from ||
                targetObjData.to ||
                ''
            )

            // WS may use @lid in from/chatId, while UI room list often uses pn (@c.us).
            // If mapping exists in wa_jid_map, normalize to pn early to improve matching and avoid duplicates.
            const derivedChatId = mapJidByCache(rawDerivedChatId)
            const from = mapJidByCache(targetObjData.from)
            const to = mapJidByCache(targetObjData.to)
            const waPayload = {
                messageType: targetObjData.messageType || targetObjData.msgType || targetObjData.type,
                message: targetObjData.message,
                event: targetObjData.event || targetObjData.action,
                userId: targetObjData.userId,
                userName: targetObjData.userName,
                chatId: derivedChatId,
                messageId: targetObjData.messageId,
                from,
                to,
                rawChatId: rawDerivedChatId,
                rawFrom: targetObjData.from,
                rawTo: targetObjData.to
            };
            console.log('[WA-WS] push message', waPayload);
            showWhatsAppNotify(1, waPayload, () => {
                router.push('/whatsapp/wahome');
            });

            try {
                window.dispatchEvent(new CustomEvent('wa-push-message', {detail: waPayload}));
            } catch (e) {
            }
            return;
        }

        if (targetObjData?.msg_front_from) {
            // 邮件消息
            messageQueue.push(targetObjData);
            document.title = `收到${messageQueue.length}封新邮件`;
            return;
        }

        // 通知消息
        store.commit("message/setNewData", data);
        store.dispatch("message/getUnReadMessageListApisDefault", {pageNo: 1, pageSize: 999});
    };

    socket.on('open', openHandler);
    socket.on('close', closeHandler);
    socket.on('error', errorHandler);
    socket.on('message', messageHandler);

    activeSocketHandlers = {
        socket,
        store,
        open: openHandler,
        close: closeHandler,
        error: errorHandler,
        message: messageHandler
    };
}

export function useWebSocket(userId, store) {
    const startWS = () => {
        if (!userId) {
            return null;
        }

        const socketUrl = getSocketUrl(userId);
        activeStore = store || activeStore;

        if (activeSocket && activeSocketUrl === socketUrl) {
            bindActiveSocket(activeSocket, userId, activeStore);
            ensureMessageQueueTimer(activeStore);

            window.socketTarget = activeSocket;
            window.wsIsClosed = false;

            const readyState = activeSocket.ws?.readyState;
            if (
                readyState === WebSocket.CLOSED ||
                readyState === WebSocket.CLOSING ||
                readyState === undefined
            ) {
                activeSocket.connect(true);
            }

            return activeSocket;
        }

        if (activeSocket && activeSocketUrl !== socketUrl) {
            unbindActiveSocket();
            activeSocket.close();
        }

        const {socket} = useSocket(socketUrl);
        activeSocket = socket;
        activeSocketUrl = socketUrl;

        bindActiveSocket(socket, userId, activeStore);
        ensureMessageQueueTimer(activeStore);

        window.socketTarget = socket;
        window.wsIsClosed = false;

        return socket;
    };

    const stopWS = () => {
        window.wsIsClosed = true;
        messageQueue = [];
        disposeMessageQueueTimer();
        unbindActiveSocket();

        if (activeSocket) {
            activeSocket.close();
        }

        activeSocket = null;
        activeSocketUrl = '';
        activeStore = null;
        window.socketTarget = null;
    };

    return {
        startWS,
        stopWS,
    };
}

document.addEventListener("visibilitychange", function () {
    if (document.visibilityState === "hidden") {
        // 离开当前标签页
        console.log('离开了');
        messageQueue = [];
        // document.title = '';
    } else if (document.visibilityState === "visible") {
        //切换到该页面时执行
        console.log('进来了');
    }
});
